import { css, useTheme, SerializedStyles } from "@emotion/react";
import React from "react";
import { UpsunTheme } from "../themes";

import { ReactComponent as UpsunLogo } from "../icons/pill-upsun.svg";
import { ReactComponent as PlatformLogo } from "../icons/pill-psh.svg";
import { colorsDark, colorsLight, pxToRem } from "../styles";
import striptags from "striptags";
import { subtitleTags } from "../helpers/striptagsDefaults";

type AnimationState = "upsun" | "platform";

type AnnouncementPillProps = {
  text: string
  css?: SerializedStyles | SerializedStyles[]
};

const AnnouncementPill: React.FC<AnnouncementPillProps> = ({ text, ...props }) => {
  const theme = useTheme() as UpsunTheme;
    const [currentCompany, setCurrentCompany] = React.useState<AnimationState>("platform");

    const initialTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
      initialTimeoutRef.current = setTimeout(() => {
        setCurrentCompany("upsun");

        intervalRef.current = setInterval(() => {
          setCurrentCompany(prevState => (prevState === "upsun" ? "platform" : "upsun"));
        }, 4000);

      }, 800);

      return () => {
        if (initialTimeoutRef.current) {
          clearTimeout(initialTimeoutRef.current);
        }
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }
    }, []);

  const getGradient = () => {
    // Platform light
    const lightPlatformLeftColor = "#FFE8E9";
    const lightPlatformMiddleColor = "#F7F8F8";
    const lightPlatformRightColor = "#FFE8E9";
    // Upsun light
    const lightUpsunLeftColor = "#F7FECC";
    const lightUpsunMiddleColor = "#F7F8F8";
    const lightUpsunRightColor = "#F7FECC";
    // Upsun dark
    const darkUpsunLeftColor = "#2B3200";
    const darkUpsunMiddleColor = "#0D0F00";
    const darkUpsunRightColor = "#313903";
    // Platform dark
    const darkPlatformLeftColor = "#322526";
    const darkPlatformMiddleColor = "#0D0F00";
    const darkPlatformRightColor = "#473334";

    let leftColor, middleColor, rightColor;
    let stopPercentage;

    if (currentCompany === "platform") {
      if(theme.name === "light") {
        // Light platform
        leftColor = lightPlatformLeftColor;
        middleColor = lightPlatformMiddleColor;
        rightColor = lightPlatformRightColor;
        stopPercentage = 47;
      } else {
        // Dark platform
        leftColor = darkPlatformLeftColor;
        middleColor = darkPlatformMiddleColor;
        rightColor = darkPlatformRightColor;
        stopPercentage = 47;
      }
    } else {
      if(theme.name === "light") {
        // Light upsun
        leftColor = lightUpsunLeftColor;
        middleColor = lightUpsunMiddleColor;
        rightColor = lightUpsunRightColor;
        stopPercentage = 53;
      } else {
        // Dark upsun
        leftColor = darkUpsunLeftColor;
        middleColor = darkUpsunMiddleColor;
        rightColor = darkUpsunRightColor;
        stopPercentage = 45;
      }
    }

    return `linear-gradient(to right, ${leftColor} 0%, ${middleColor} ${stopPercentage}%, ${rightColor}) 100%`;
  }

  const containerStyles = css`
    font-size: ${pxToRem(13)};
    line-height: ${pxToRem(20)};

    padding: ${pxToRem(8)} ${pxToRem(24)};
    padding-left: ${pxToRem(8)};
    border-radius: ${pxToRem(60)};

    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      border-radius: ${pxToRem(100)};
      background: ${getGradient()};
      z-index: -1;

      transition: all 1s linear;
    }

    display: inline-flex;
    flex-direction: row;
    align-items: center;
    gap: ${pxToRem(16)};

    background-color: ${theme.name === "light" ? "#FFFFFF" : "#0E1113"};
    box-shadow: ${currentCompany === "platform" ? "0px 4px 100px 0px rgba(254, 181, 184, 0.2)" : "0 4px 100px 0 rgba(238,252,153, 0.5)"};

    transition: box-shadow 1s linear;
  `;

  const logoContainerStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 33px;
    height: 33px;

    svg {
      position: absolute;
      top: 0;
      left: 0;
    }
  `;

  const upsunLogoStyles = css`
    opacity: ${currentCompany === "upsun" ? 1 : 0};
    transition: opacity 500ms linear;
  `;

  const platformLogoStyles = css`
    opacity: ${currentCompany === "platform" ? 1 : 0};
    transition: opacity 500ms linear;
  `;

  const textStyles = css`
    background: none;
    
    a {
      transition: none;
      color: ${theme.name === "light" ? colorsLight.NeutralTxtDefault : colorsDark.NeutralTxtDefault};
      background: none !important;
      &:hover {
        color: ${theme.name === "light" ? colorsLight.PrimaryTxtDefault : colorsDark.PrimaryTxtDefault};
      }
    }
  `;

  const sanitizedText = striptags(text, subtitleTags, "");

  return (
    <div css={containerStyles} {...props}>
      <div css={logoContainerStyles}>
        <UpsunLogo css={upsunLogoStyles} />
        <PlatformLogo css={platformLogoStyles}/>
      </div>
      <div css={textStyles} dangerouslySetInnerHTML={{__html: sanitizedText}} />
    </div>
  )
};

export default AnnouncementPill;